import React, { useState, ChangeEvent, FormEvent } from 'react';
import {
    TextField,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    AlertTitle,
    Card,
    CardContent,
    Typography,
    Box,
    Grid,
    Paper,
    Divider,
    Link,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { useMutationCoordinate } from '../../clients/graphql/graphql.hooks';
import { jediUpsertAssetMutation } from '../../clients/graphql/pages/assets-queries';
import { Link as RouterLink } from 'react-router-dom';

const AssetAdd: React.FC = () => {
    const [symbol, setSymbol] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [figi, setFigi] = useState<string>('');
    const [marketCap, setMarketCap] = useState<string>('');
    const [pictureData, setPictureData] = useState<string | null>(null);
    const [pictureName, setPictureName] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    const [updateAsset] = useMutationCoordinate(jediUpsertAssetMutation, {});

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            setPictureData(result.split(',')[1]);
            setPictureName(file.name);
        };
        reader.onerror = () => setError('Error reading file');
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true);
        setError('');

        try {
            await updateAsset({
                variables: {
                    symbol: symbol.toUpperCase(),
                    name,
                    description,
                    pictureData,
                    pictureName,
                    figi,
                    marketCap,
                },
            });

            setLoading(false);
            setSuccess(true);
            setSymbol('');
            setName('');
            setDescription('');
            setFigi('');
            setMarketCap('');
            setPictureData(null);
            setPictureName(null);
        } catch (err: any) {
            setLoading(false);
            setError(err.message);
            console.error(err);
        }
    };

    const handleDialogClose = () => {
        setSuccess(false);
        setError('');
        setLoading(false);
    };

    const handleCancel = () => {
        setSymbol('');
        setName('');
        setDescription('');
        setPictureData(null);
        setPictureName(null);
    };

    const isFormValid = () => {
        return symbol.trim() && name.trim() && description.trim() && pictureData;
    };

    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
              <Link component={RouterLink} to="/assets" underline="none" color="inherit">
                  Assets
              </Link>
              <Typography color="text.primary">Add New Asset</Typography>
          </Breadcrumbs>

          <Box sx={{ mb: 4 }}>
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                  Add New Asset
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Create a new asset with symbol, name, description, figi, and image.
              </Typography>
          </Box>

          <Card sx={{ boxShadow: 3 }}>
              <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
                      Asset Information
                  </Typography>

                  <form onSubmit={handleSubmit}>
                      <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                              <Paper elevation={1} sx={{ p: 3, height: 'fit-content' }}>
                                  <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                                      Basic Information
                                  </Typography>

                                  <TextField
                                    label="Symbol"
                                    value={symbol}
                                    onChange={(e) => setSymbol(e.target.value)}
                                    required
                                    fullWidth
                                    margin="normal"
                                    variant="outlined"
                                    sx={{ mb: 2 }}
                                    placeholder="AAPL"
                                    helperText="Stock Symbol"
                                  />

                                  <TextField
                                    label="Asset Name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    fullWidth
                                    margin="normal"
                                    variant="outlined"
                                    sx={{ mb: 2 }}
                                    placeholder="Apple Inc."
                                    helperText="Company name"
                                  />

                                  <TextField
                                    label="Figi"
                                    value={figi}
                                    onChange={(e) => setFigi(e.target.value)}
                                    required
                                    fullWidth
                                    margin="normal"
                                    variant="outlined"
                                    sx={{ mb: 2 }}
                                    placeholder="BBG000B9XRY4"
                                    helperText="Ticker FIGI"
                                  />

                                  <TextField
                                    label="MarketCap"
                                    value={marketCap}
                                    onChange={(e) => setMarketCap(e.target.value)}
                                    required
                                    fullWidth
                                    margin="normal"
                                    variant="outlined"
                                    sx={{ mb: 2 }}
                                    placeholder="13518475614.54"
                                    helperText="Asset MarketCap"
                                  />

                                  <TextField
                                    label="Description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    required
                                    fullWidth
                                    margin="normal"
                                    variant="outlined"
                                    multiline
                                    rows={4}
                                    sx={{ mb: 2 }}
                                    placeholder="Enter a brief description of the asset..."
                                    helperText="Description of the asset"
                                  />
                              </Paper>
                          </Grid>

                          <Grid item xs={12} md={6}>
                              <Paper elevation={1} sx={{ p: 3, height: 'fit-content' }}>
                                  <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                                      Asset Image
                                  </Typography>

                                  <input
                                    accept="image/*"
                                    type="file"
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                    id="raised-button-file"
                                  />
                                  <label htmlFor="raised-button-file">
                                      <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<PhotoCameraIcon />}
                                        sx={{ mb: 2 }}
                                      >
                                          Upload Image
                                      </Button>
                                  </label>

                                  {pictureData && (
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                            Selected Image
                                        </Typography>
                                        <img
                                          src={`data:image/*;base64,${pictureData}`}
                                          alt="Selected"
                                          style={{
                                              width: '120px',
                                              height: '120px',
                                              borderRadius: '12px',
                                              border: '2px solid #1976d2',
                                              objectFit: 'cover',
                                          }}
                                        />
                                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                            {pictureName}
                                        </Typography>
                                    </Box>
                                  )}
                              </Paper>
                          </Grid>
                      </Grid>

                      <Alert severity="info" sx={{ mt: 3, mb: 3 }}>
                          <AlertTitle>Requirements</AlertTitle>
                          All fields are required. Images must be under 100KB and in a common format (JPEG, PNG, GIF).
                      </Alert>

                      <Divider sx={{ my: 3 }} />

                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            onClick={handleCancel}
                            disabled={loading}
                            startIcon={<CancelIcon />}
                          >
                              Cancel
                          </Button>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loading || !isFormValid()}
                            startIcon={loading ? <CircularProgress size="1rem" /> : <AddIcon />}
                            sx={{ minWidth: 120 }}
                          >
                              {loading ? 'Saving...' : 'Save Asset'}
                          </Button>
                      </Box>
                  </form>
              </CardContent>
          </Card>

          <Dialog open={success} onClose={handleDialogClose} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="success" />
                  Success
              </DialogTitle>
              <DialogContent>
                  <Typography>
                      Asset updated successfully! The asset information has been saved.
                  </Typography>
              </DialogContent>
              <DialogActions>
                  <Button onClick={handleDialogClose} color="primary" variant="contained">
                      OK
                  </Button>
              </DialogActions>
          </Dialog>

          <Dialog open={Boolean(error)} onClose={handleDialogClose} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ErrorIcon color="error" />
                  Error
              </DialogTitle>
              <DialogContent>
                  <Typography color="error">
                      {error}
                  </Typography>
              </DialogContent>
              <DialogActions>
                  <Button onClick={handleDialogClose} color="primary" variant="contained">
                      Close
                  </Button>
              </DialogActions>
          </Dialog>
      </Box>
    );
};

export default AssetAdd;